import { GALLERY_STATES, type GalleryState } from "@volgo/core/contract";
import { STATE_LABELS } from "../ui/labels";
import { removeTag, type SearchQuery, splitTag } from "./query";

/** One removable condition as the search bar draws it. Derived from the query, never stored. */
export interface QueryToken {
  key: string;
  kind: "gid" | "text" | "tag" | "xtag" | "rating" | "states" | "blocked";
  /** grey prefix (`language` in `language:korean`) */
  namespace?: string;
  label: string;
  removeLabel: string;
  remove: (q: SearchQuery) => SearchQuery;
}

/** The server's default when no state is chosen. */
export const DEFAULT_STATES: readonly GalleryState[] = ["listed", "expunged"];

export function effectiveStates(q: SearchQuery): readonly GalleryState[] {
  return q.states.length ? q.states : DEFAULT_STATES;
}

/** Turns one state on or off. At least one stays on; the default pair is stored as "nothing chosen". */
export function toggleState(q: SearchQuery, state: GalleryState): SearchQuery {
  const current = effectiveStates(q);
  const on = current.includes(state);
  const next = GALLERY_STATES.filter((s) => (s === state ? !on : current.includes(s)));
  if (next.length === 0) return q;
  const isDefault = next.length === DEFAULT_STATES.length && DEFAULT_STATES.every((s) => next.includes(s));
  return { ...q, states: isDefault ? [] : next };
}

/** Every condition that lives in the search bar, in the order it is drawn. Categories and sort are not here. */
export function tokensOf(q: SearchQuery): QueryToken[] {
  const out: QueryToken[] = [];
  if (q.gid !== null) {
    out.push({
      key: "gid",
      kind: "gid",
      namespace: "GID",
      label: String(q.gid),
      removeLabel: "GID 조건 지우기",
      remove: (x) => ({ ...x, gid: null }),
    });
  }
  if (q.text) {
    out.push({
      key: "text",
      kind: "text",
      namespace: "제목",
      label: q.text,
      removeLabel: "제목 조건 지우기",
      remove: (x) => ({ ...x, text: "" }),
    });
  }
  for (const tag of q.tags) {
    const { namespace, name } = splitTag(tag);
    out.push({
      key: `tag:${tag}`,
      kind: "tag",
      ...(namespace ? { namespace } : {}),
      label: name,
      removeLabel: `${tag} 지우기`,
      remove: (x) => removeTag(x, tag),
    });
  }
  for (const tag of q.xtags) {
    const { namespace, name } = splitTag(tag);
    out.push({
      key: `xtag:${tag}`,
      kind: "xtag",
      ...(namespace ? { namespace } : {}),
      label: name,
      removeLabel: `제외 태그 ${tag} 지우기`,
      remove: (x) => removeTag(x, tag),
    });
  }
  if (q.ratingMin !== null) {
    out.push({
      key: "rating",
      kind: "rating",
      label: `평점 ${q.ratingMin} 이상`,
      removeLabel: "평점 조건 지우기",
      remove: (x) => ({ ...x, ratingMin: null }),
    });
  }
  if (q.states.length) {
    out.push({
      key: "states",
      kind: "states",
      namespace: "상태",
      label: q.states.map((s) => STATE_LABELS[s]).join(", "),
      removeLabel: "상태 조건 지우기",
      remove: (x) => ({ ...x, states: [] }),
    });
  }
  if (q.blocked !== null) {
    out.push({
      key: "blocked",
      kind: "blocked",
      label: q.blocked === "show" ? "차단 갤러리 보이기" : "차단 갤러리 숨기기",
      removeLabel: "차단 갤러리 조건 지우기",
      remove: (x) => ({ ...x, blocked: null }),
    });
  }
  return out;
}
